
import { nextTick } from 'vue';
import {
  CONFIGURED_WORKFLOW_URL,
  WORKFLOW_ENTITIES_FOR_ASSIGNMENT_URL,
  STEP_ASSIGNED_SIGNERS_URL,
  TEMPLATES_ASSIGNED_TO_ENTITY_TYPE_URL,
  PREVIEW_DOCUSEAL_URL,
  SEND_CONTRACT,
  RESEND_DOCUSEAL_EMAIL_URL,
  TRACK_DOCUSEAL_STATUS_URL,
  DOWNLOAD_SIGNED_DOCUMENT_URL,
  get_headers,
  get_user_role,
} from '@/utils/constants/constants.js';
import axios from 'axios';
import formatting_mixins from '@/utils/mixins/formatting_mixins';
import alert_mixin from '@/utils/mixins/alert_mixin';
import e_signing_mixins from '@/utils/mixins/e_signing_mixins';

export default {
  name: 'SigningFlow',

  mixins: [alert_mixin, formatting_mixins, e_signing_mixins],
  

  data: () => ({
    headers: get_headers(),
    role: get_user_role(),

    // template selection
    assigned_templates: [],
    configured_templates: [],
    selected_template_id: null,

    // timeline / steps
    steps: [],
    local_steps: [],
    step_element_refs: [],
    node_top_offsets: [],
    resize_observer: null,
    selected_template: null,

    // preview
    preview_url: null,
    is_preview_loading: false,

    // manage modal
    show_manage_modal: false,
    current_step: null,
    manage_form: {
      name: '',
      surname: '',
      email: '',
    },

    // context
    project_id: Number(sessionStorage.getItem('project_id')) || null,
    entity_type_id: null,
    entity_profile_id: null,

    // table data for assign modal
    assignable_user_rows: [],
    assign_users_table_key: 0,

    // ids for payloads
    workflow_id: null,
    template_id: null,
    workflow_assignment_id: null,
  }),

  mounted() {
    if (this.$route.query.entity_type_id) {
      this.entity_type_id = Number(this.$route.query.entity_type_id);
    } else {
      this.entity_type_id =  Number(sessionStorage.getItem('entity_type_id')) || null;
    }

    if (this.$route.query.entity_profile_id) {
      this.entity_profile_id = Number(this.$route.query.entity_profile_id);
    } else {
      this.entity_profile_id =  Number(sessionStorage.getItem('entity_profile_id')) || null;
    }

    this.local_steps = JSON.parse(JSON.stringify(this.steps));

    nextTick(() => {
      this.measure_nodes();
      window.addEventListener('resize', this.handle_resize);

      if ('ResizeObserver' in window) {
        this.resize_observer = new ResizeObserver(this.measure_nodes);
        this.step_element_refs.forEach(element => {
          if (element) {
            this.resize_observer.observe(element);
          }
        });
      }
    });

    this.load_assigned_templates();
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.handle_resize);
    if (this.resize_observer) {
      this.resize_observer.disconnect();
    }
  },

  methods: {

    reset_steps() {
      this.steps = [];
      this.local_steps = [];
      this.step_element_refs = [];
      this.node_top_offsets = [];
    },

    find_current_step_index(workflow_steps, entity_type_id) {
      if (!entity_type_id) {
        return 0;
      }

      const found_index = workflow_steps.findIndex(
        workflow_step =>
          workflow_step.entity_type &&
          Number(workflow_step.entity_type.id) === Number(entity_type_id),
      );

      return found_index !== -1 ? found_index : 0;
    },

    build_steps_from_workflow(template_obj, entity_type_id) {
      const workflow = template_obj.workflow || {};
      const workflow_steps = workflow.steps || [];

      this.template_id = template_obj.id || null;
      this.workflow_id = workflow.id || null;
      this.workflow_assignment_id = template_obj.assignment_id || null;

      const current_step_index = this.find_current_step_index(
        workflow_steps,
        entity_type_id,
      );

      this.steps = workflow_steps.map((workflow_step, index) => ({
        id: workflow_step.id,
        title:
          workflow_step.entity_type?.entity_type_name ||
          `Step ${index + 1}`,
        type_id: workflow_step.entity_type?.id,
        state: index === current_step_index ? 'current' : 'upcoming',
        owner: '',
        email: '',
        signers: [],
        signers_meta: [],
        expanded: false,
      }));

      this.local_steps = JSON.parse(JSON.stringify(this.steps));
      this.step_element_refs = [];
    },

    map_signers_by_step_id(api_steps) {
      const signers_by_step_id = {};
      api_steps.forEach(step_from_api => {
        const step_signers = step_from_api.signers || [];
        signers_by_step_id[step_from_api.id] = step_signers.map(
          signer_from_api => ({
            id: signer_from_api.signer_id || signer_from_api.id,
            name: signer_from_api.full_name || signer_from_api.email,
            email: signer_from_api.email,
            docuseal_status_label: signer_from_api.docuseal_status || signer_from_api.display_status?.toLowerCase() || 'awaiting',
            status_label: signer_from_api.status_label,
            signing_order: signer_from_api.signing_order,
          }),
        );
      });
      return signers_by_step_id;
    },

    collect_assignable_users(entity_results) {
      const users_map = new Map();

      entity_results.forEach(entity_item => {
        (entity_item.users || []).forEach(user_item => {
          if (!user_item.user_id || users_map.has(user_item.user_id)) {
            return;
          }
          users_map.set(user_item.user_id, {
            id: user_item.user_id,
            first_name: user_item.first_name || '',
            last_name: user_item.last_name || '',
            email: user_item.email || '',
            is_assigned: false,
            is_loading: false,
          });
        });

        (entity_item.children || []).forEach(child_entity => {
          (child_entity.users || []).forEach(user_item => {
            if (!user_item.user_id || users_map.has(user_item.user_id)) {
              return;
            }
            users_map.set(user_item.user_id, {
              id: user_item.user_id,
              first_name: user_item.first_name || '',
              last_name: user_item.last_name || '',
              email: user_item.email || '',
              is_assigned: false,
              is_loading: false,
            });
          });
        });
      });

      return Array.from(users_map.values());
    },

    apply_assigned_signers(assignable_users, assigned_data) {
      if (assigned_data.status !== 'success') {
        this.show_error(
          assigned_data.message ||
            'Error loading assigned signers for step',
        );
        return assignable_users;
      }

      const signer_rows = Array.isArray(assigned_data.data)
        ? assigned_data.data
        : [];

      const assigned_user_ids = signer_rows
        .map(signer_item => signer_item.user_id)
        .filter(id => id != null)
        .map(Number);

      const assigned_user_id_set = new Set(assigned_user_ids);

      return assignable_users.map(user_row => ({
        ...user_row,
        is_assigned: assigned_user_id_set.has(Number(user_row.id)),
      }));
    },


    toggle_expand(step_item) {
      step_item.expanded = !step_item.expanded;
      nextTick(this.measure_nodes);
    },

    handle_resize() {
      this.measure_nodes();
    },

    measure_nodes() {
      const timeline_element =
        this.$el.querySelector('.signing-timeline');
      if (!timeline_element) return;

      const timeline_top =
        timeline_element.getBoundingClientRect().top +
        window.scrollY;

      this.node_top_offsets = this.step_element_refs.map(element => {
        if (!element) return 0;
        const element_top =
          element.getBoundingClientRect().top + window.scrollY;
        return Math.max(
          0,
          Math.round(element_top - timeline_top - 14),
        );
      });
    },

    async load_assigned_templates() {
      if (!this.entity_type_id) {
        return;
      }

      return axios
        .get(TEMPLATES_ASSIGNED_TO_ENTITY_TYPE_URL, {
          headers: this.headers,
          params: {
            project_id: this.project_id,
            entity_type_id: this.entity_type_id,
            is_assigned: true,
            entity_profile_id: this.entity_profile_id,
          },
        })
        .then(response => {
          const payload = response.data || {};

          if (payload.status !== 'success') {
            this.show_error(
              payload.message || 'Error loading assigned templates',
            );
            return;
          }

          this.assigned_templates = (payload.results || []).map(t => ({
            ...t,
            submission_id: t.submission_id || null,
            submission_status: t.submission_status || 'pending'
          }));
        })
        .catch(error => {
          this.show_error(
            'Error loading assigned templates: ' +
              (error.response?.data?.message || error.message),
          );
        });
    },

    load_configured_workflow_for_template(template_id) {
      if (!this.entity_type_id || !template_id) {
        return Promise.resolve(null);
      }

      return axios
        .get(CONFIGURED_WORKFLOW_URL, {
          headers: this.headers,
          params: {
            project_id: this.project_id,
            entity_type_id: this.entity_type_id,
            template_id,
          },
        })
        .then(response => {
          const response_data = response.data || {};


          if (response_data.status !== 'success') {
            this.show_error(
              response_data.message ||
                'Error during retrieval of configured workflow',
            );
            this.reset_steps();
            // Explicit return for this branch
            return null;
          }

          const items = Array.isArray(response_data.data)
            ? response_data.data
            : [];

          if (items.length === 0) {
            this.show_error(
              'No configured workflow found for this template. Please assign a workflow first.',
            );
            this.reset_steps();
            // Explicit return for this branch
            return null;
          }

          const template_obj = items[0] || {};
          this.build_steps_from_workflow(
            template_obj,
            this.entity_type_id,
          );

          nextTick(this.measure_nodes);

          return this.load_step_signers({ silent: true });

        })
        .catch(error => {
          this.show_error(
            'Error during retrieval of configured workflow: ' +
              (error.response?.data?.message || error.message),
          );
          // Explicit return for error path
          return null;
        });
    },


    async handle_template_click(template_row) {
      const template_id = template_row.template_id;
      this.selected_template_id = template_id;

      this.workflow_assignment_id = template_row.workflow_assignment_id;

      this.workflow_id = template_row.workflow_id;

      if (template_row.submission_id) {
        try {
          await axios.post(
            TRACK_DOCUSEAL_STATUS_URL,
            { submission_id: template_row.submission_id },
            { headers: this.headers }
          );
        } catch (error) {
          console.error("Failed to refresh DocuSeal status:", error);
        }
      }
      // returns a thenable (the axios chain)
      await this.load_configured_workflow_for_template(template_id);

    },


    async fetch_assignable_users(entity_type_id_for_step) {
      const entity_type_id = entity_type_id_for_step;
      const entity_profile_id = this.entity_profile_id;

      try {
        const entities_response = await axios.get(
          WORKFLOW_ENTITIES_FOR_ASSIGNMENT_URL,
          {
            headers: this.headers,
            params: {
              entity_type_id,
              entity_profile_id,
            },
          },
        );

        const entities_payload = entities_response.data || {};

        if (entities_payload.status !== 'success') {
          this.show_error('Error retrieving entities for assignment');
          return;
        }

        const entity_results = entities_payload.results || [];
        let assignable_users =
          this.collect_assignable_users(entity_results);

        if (
          this.template_id &&
          this.workflow_id &&
          this.current_step
        ) {
          const assigned_response = await axios.get(
            STEP_ASSIGNED_SIGNERS_URL,
            {
              headers: this.headers,
              params: {
                workflow_assignment_id : this.workflow_assignment_id ,
                step_id: this.current_step.id,
                entity_profile_id: this.entity_profile_id,
              },
            },
          );

          const assigned_data = assigned_response.data || {};
          assignable_users = this.apply_assigned_signers(
            assignable_users,
            assigned_data,
          );
        }

        this.assignable_user_rows = assignable_users;
        this.assign_users_table_key += 1;
      } catch (error) {
        this.show_error(
          'Error loading assignable users: ' +
            (error.response?.data?.message || error.message),
        );
      }
    },

    async open_manage(step_item) {
      if (this.role?.includes('Entity')) {
        return; // do nothing
      }
      this.current_step = step_item;

      const full_owner_name = step_item.owner || '';
      const owner_name_parts = full_owner_name.trim().split(' ');

      this.manage_form.name = owner_name_parts[0] || '';
      this.manage_form.surname =
        owner_name_parts.slice(1).join(' ') || '';
      this.manage_form.email = step_item.email || '';

      const entity_type_id_for_step = step_item.type_id;
      await this.fetch_assignable_users(entity_type_id_for_step);
    },

    close_manage() {
      this.show_manage_modal = false;
      this.current_step = null;
    },

    preview_template(template) {
      this.selected_template = template;
      this.is_preview_loading = true;
      this.preview_url = null;

      const request_payload = {
        template_id: template.template_id,
        entity_id: this.entity_profile_id
      };

      axios.post(
        PREVIEW_DOCUSEAL_URL,
        request_payload,
        { headers: this.headers }
      )
      .then(response => {

        const payload = response.data;

        if (payload.status !== "success") {
          this.show_error(payload.message || "Failed to generate preview");
          return;
        }

        //Use embed_src for iframe embedding
        this.preview_url = payload.embed_src;

      })
      .catch(error => {
        const error_message = error.response?.data?.message || error.message;
        this.show_error(error_message);
      })
      .finally(() => {
        this.is_preview_loading = false;
      });
    },

    confirm_send_contract(template) {
      this.show_confirm(
        "Are you sure you want to send this contract?",
        "This will send the contract to all signers.",
        () => {
          // Confirm → send the contract
          this.send_contract(template);
        },
        () => {
          // Cancel → go back (do nothing or close modal)
        }
      );
    },

    send_contract(template) {
      const workflow_assignment_id = template.id;

      if (!workflow_assignment_id) {
        this.show_error("Missing workflow assignment ID for this template.");
        return;
      }

      if (!this.entity_profile_id) {
        this.show_error("Missing entity profile ID.");
        return;
      }

      const payload = {
        workflow_assignment_id: workflow_assignment_id,
        entity_profile_id: this.entity_profile_id,
        send_email: true,
      };

      axios.post(SEND_CONTRACT, payload, { headers: this.headers })
        .then(res => {
          if (res.data.status !== "success") {
            this.show_error(res.data.message || "Failed to send contract");
            return;
          }

          this.show_success("Contract sent successfully!");
          this.close_modal("preview_template_modal" );
        })
        .catch(err => {
          const msg =
            err.response?.data?.details ||
            err.response?.data?.message ||
            err.message;
          this.show_error(msg);
        });
    },

    get_submitter_icon(status) {
      const map = {
        completed: "bi-check-circle-fill text-success",
        opened: "bi-envelope-open text-primary",
        sent: "bi-send-fill text-purple",
        awaiting: "bi-clock-fill text-muted",
        declined: "bi-x-circle-fill text-danger",
      };
      return map[Reflect.get(map, status)] || "bi-clock-fill text-muted";
    },

    get_submitter_badge(status) {
      const map = {
        completed: "bg-success-light text-success",
        sent: "bg-purple-light text-purple",
        opened: "bg-primary-light text-primary",
        awaiting: "bg-gray-light text-muted",
        declined: "bg-danger-light text-danger",
      };
      return Reflect.get(map, status) || "bg-gray-light text-muted";  },

    format_status(status) {
      if (!status) return "Awaiting";
      return status.charAt(0).toUpperCase() + status.slice(1);
    },



    normalise_status(raw_status) {
      if (!raw_status) return '';
      return String(raw_status).toLowerCase().trim();
    },

    compute_status_from_signers(signers_meta) {
  const signer_statuses = (signers_meta || [])
    .map(signer => this.normalise_status(signer.status_label))
    .filter(Boolean);

  if (!signer_statuses.length) return 'awaiting';

  if (signer_statuses.some(status => status === 'declined')) return 'declined';
  if (signer_statuses.every(status => status === 'completed')) return 'completed';
  if (signer_statuses.some(status => status === 'sent' || status === 'opened')) return 'pending';

  return 'awaiting';
},

get_step_overall_status(step) {
  const meta = step.signers_meta || [];
  return this.compute_status_from_signers(meta);
},

get_step_status_icon_class(status) {
  const normalised_status = this.normalise_status(status);

  if (normalised_status === 'completed') return 'bi bi-check-circle';
  if (normalised_status === 'declined') return 'bi bi-x-circle';

  return 'bi bi-clock-history';
},

get_template_status_label(template) {
  // Prefer backend value
  if (template && template.submission_status) {
    const value = String(template.submission_status);
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  // Use local steps if available
  if (!this.local_steps.length) return 'Pending';

  const step_statuses = this.local_steps.map(step =>
    this.get_step_overall_status(step)
  );

  if (step_statuses.some(status => status === 'declined')) return 'Declined';
  if (step_statuses.every(status => status === 'completed')) return 'Completed';

  return 'Pending';
},

get_template_status_class(template) {
  const label = this.get_template_status_label(template).toLowerCase();

  if (label === 'completed') return 'text-success';
  if (label === 'pending') return 'text-warning';
  if (label === 'declined') return 'text-danger';

  return 'text-muted';
},

get_submitter_icon_class(status) {
  const normalised_status = this.normalise_status(status);

  if (normalised_status === 'completed') return 'bi bi-check-circle text-success me-2';
  if (normalised_status === 'opened') return 'bi bi-clock-history text-primary me-2';
  if (normalised_status === 'sent') return 'bi bi-send text-primary me-2';
  if (normalised_status === 'awaiting') return 'bi bi-clock text-muted me-2';
  if (normalised_status === 'declined') return 'bi bi-x-circle text-danger me-2';

  return 'bi bi-clock text-muted me-2';
},

get_submitter_badge_class(status) {
  const normalised_status = this.normalise_status(status);

  if (normalised_status === 'completed') return 'signing-status-completed';
  if (normalised_status === 'sent') return 'signing-status-sent';
  if (normalised_status === 'opened') return 'signing-status-opened';
  if (normalised_status === 'awaiting') return 'signing-status-awaiting';
  if (normalised_status === 'declined') return 'signing-status-declined';

  return 'signing-status-awaiting';
},

format_status_label(status) {
  if (!status) return 'Awaiting';

  const normalised_status = this.normalise_status(status);
  return normalised_status.charAt(0).toUpperCase() + normalised_status.slice(1);
},


get_template_steps(template) {
    if (this.selected_template_id === template.template_id) {
      return this.local_steps || [];
    }
    return [];
  },

  /**
   * Compute overall status for a step using signers_meta.
   * Reuses your existing compute_status_from_signers().
   */
  get_step_group_status(step) {
    if (!step || !Array.isArray(step.signers_meta)) {
      return 'awaiting';
    }
    return this.compute_status_from_signers(step.signers_meta);
  },



  /**
   * Map step group status to Bootstrap icon class.
   */
  get_step_group_status_icon(step) {
    const status = this.get_step_group_status(step);
    const icons = {
      completed: 'bi-check-circle-fill',
      pending: 'bi-clock-fill',
      awaiting: 'bi-clock-fill',
      declined: 'bi-x-circle-fill',
    };
    const icon = Reflect.get(icons, status) || Reflect.get(icons, 'awaiting');
    return 'bi ' + icon;
  },

  extract_signer_status(raw_signer) {
    if (!raw_signer) return 'awaiting';

    // Try several possible field names coming from backend
    const raw =
      raw_signer.status ??
      raw_signer.docuseal_status ??
      raw_signer.status_label ??
      '';

    if (!raw) return 'awaiting';
    return String(raw).toLowerCase().trim();
  },


  // Optional: If you still want a refresh method for after resending emails:
  async refresh_step_signers() {
    if (!this.workflow_assignment_id || !this.entity_profile_id) {
      return;
    }

    await this.load_step_signers({ silent: true });
  },

  // Update resend_submitter_email to use the refresh:
resend_submitter_email(signer) {
  if (!signer || !signer.id) {
    this.show_error("Invalid signer selected.");
    return;
  }

  axios.post(
    RESEND_DOCUSEAL_EMAIL_URL,
    { signer_id: signer.id },
    { headers: this.headers }
  )
  .then((response) => {
    const payload = response.data;

    if (payload.status !== "success") {
      this.show_error(
        this.normalise_resend_error(payload.message || payload.error)
      );
      return;
    }

    this.show_success("Signing email resent successfully!");
    return this.refresh_step_signers();
  })
  .catch((error) => {
    const raw =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.response?.data?.details ||
      error.message;

    this.show_error(this.normalise_resend_error(raw));
  });
},



  normalise_resend_error(msg) {
    if (!msg) return "Failed to resend email.";

    const text = msg.toLowerCase();

    if (text.includes("already completed")) {
      return "This signer has already completed signing.";
    }

    if (text.includes("submitter") && text.includes("not found")) {
      return "The signing request for this signer could not be found.";
    }

    if (text.includes("email")) {
      return "Unable to resend email. Please check the signer’s email address.";
    }

    if (text.includes("docuseal")) {
      return "DocuSeal returned an unexpected response. Please try again.";
    }

    return msg; // fallback: clean original message
  },


  async download_signed_document(submission_id) {
    if (!submission_id) {
      this.show_error("Missing submission ID");
      return;
    }

    return axios
      .get(DOWNLOAD_SIGNED_DOCUMENT_URL, {
        params: { submission_id },
        responseType: "blob",
        headers: this.headers,
      })
      .then((response) => {
        // Extract filename from header OR fallback name
        let filename = "signed_document.pdf";
        const disposition =
          response.headers["content-disposition"] ||
          response.headers["Content-Disposition"];

        if (disposition && disposition.includes("filename=")) {
          filename = disposition.split("filename=")[1].replace(/"/g, "");
        }

        // Convert blob to downloadable link
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        this.show_success("Signed document downloaded successfully");
      })
      .catch(async (error) => {
        let message = "Failed to download signed PDF";

        try {
          if (error.response && error.response.data && typeof error.response.data.text === "function") {
            // Try to extract server JSON error from blob
            const text = await error.response.data.text();
            const json = JSON.parse(text || "{}");
            message = json.message || json.details || message;
          } else {
            message =
              error.response?.data?.message ||
              error.response?.data?.details ||
              error.message ||
              message;
          }
        } catch (parseErr) {
          message = error.message || message;
        }

        this.show_error(message);
      });
  },



  },
};